import { firestore, fieldValue, COMM_COLL, USERS_COLL, MODEL_COLL } from "_firebase";
import { isEmpty, cloneDeep } from "lodash";
import moment from "moment";

import { getProvCode } from "utils/weather";
import { getCliResults } from "utils/results/api";
import { getBaseRunId } from "utils/results/runIdentity";
import { getRegion } from "utils/communities";
import {
    ensureCommunityRunCapacityOnServer,
    mergeRunCapacityIntoCommunityDetails,
} from "utils/communities/ensureCommunityRunCapacity";
import { setAllCommunityEcmMeasuresActive } from "utils/communities/communityValidation";

import { getUpgrades } from "features/Community/Upgrades/_ducks/thunk";
import { actions as userActions } from "store/users";
import { DEFAULT_ECM_INPUT_MAP } from "utils/parametric/constants";
import {
    batchDeleteCommunityError,
    batchDeleteCommunityStart,
    batchDeleteCommunitySuccess,
    createCommunityError,
    createCommunityStart,
    createCommunitySuccess,
    deleteCommunityError,
    deleteCommunityStart,
    deleteCommunitySuccess,
    fetchCommunityError,
    fetchCommunityStart,
    fetchCommunitySuccess,
    moveCommunityError,
    moveCommunityStart,
    moveCommunitySuccess,
    saveEcmInputMapError,
    saveEcmInputMapStart,
    saveEcmInputMapSuccess,
    startCommunityLoading,
    endCommunityLoading,
    setCommunityError,
    setCommunityName,
    setLocation,
    setModelDuplicates,
    setLastEdited,
    setInitData,
    addCommModel,
    removeCommModel,
    updateCommunityDetails,
    mergeCommunityEcmValidationResults,
    updateRunResults,
    saveCommunitySuccess,
    saveCommunityStart,
} from "./actions";

const { fetchUserCommDir } = userActions;

// CREATE COMMUNITY (Add to user and communities fields in backend)
export const createCommunity = (communityToCreate, userUid) => async (dispatch) => {
    try {
        dispatch(createCommunityStart());

        const { name, totalModels, parentFolderId, createdAt, sharedWith, regions, weatherLocation } =
            communityToCreate;

        const sharedWithDeduped = Array.from(new Set([...(sharedWith || []), userUid]));

        const newCommunityDocument = {
            ...communityToCreate,
            sharedWith: sharedWithDeduped,
            owner: { uid: userUid },
            // Client create starts at 0; complimentary / paid capacity is applied server-side.
            availableRuns: 0,
        };

        // Add Community to Community collection
        const { id } = await firestore.collection(COMM_COLL).add(newCommunityDocument);
        // console.log("Added community with ID: ", id);

        // Initiate Community Model Dir
        await firestore.doc(`${COMM_COLL}/${id}`).collection("modelDir").doc("directory").set({ models: {} });

        // Add Community to User collection
        await firestore
            .doc(`${USERS_COLL}/${userUid}`)
            .collection("commDir")
            .doc("directory")
            .set(
                {
                    communities: {
                        [`${id}`]: {
                            name,
                            parentFolderId,
                            totalModels: 0,
                            lastEdited: createdAt,
                            region: "CAN",
                        },
                    },
                },
                { merge: true }
            );

        await ensureCommunityRunCapacityOnServer(id);

        dispatch(createCommunitySuccess());

        return id;
    } catch (error) {
        console.log("Community could not be created successfully", error.message);
        dispatch(createCommunityError(error));
    }
};

export const fetchCommunity = (communityId) => async (dispatch) => {
    try {
        dispatch(fetchCommunityStart());

        const capacityResult = await ensureCommunityRunCapacityOnServer(communityId);

        const communitySnapshot = await firestore.doc(`${COMM_COLL}/${communityId}`).get();

        const modelDirSnapshot = await firestore
            .doc(`${COMM_COLL}/${communityId}`)
            .collection("modelDir")
            .doc("directory")
            .get();

        const ecmInputMapSnapshot = await firestore
            .doc(`${COMM_COLL}/${communityId}`)
            .collection("config")
            .doc("ecmInputMap")
            .get();

        const configurationSnapshot = await firestore
            .doc(`${COMM_COLL}/${communityId}`)
            .collection("config")
            .doc("configuration")
            .get();

        const communityParametricResultsSnapshot = await firestore
            .doc(`${COMM_COLL}/${communityId}`)
            .collection("runResults")
            .get();

        const parametricResults = communityParametricResultsSnapshot.docs.reduce((acc, doc) => {
            acc[doc.id] = doc.data();
            return acc;
        }, {});

        // const upgradesSnapshot = await firestore
        //     .doc(`${COMM_COLL}/${communityId}`)
        //     .collection("upgrades")
        //     .doc("upgradePackages")
        //     .get();

        await dispatch(getUpgrades({ communityId }));

        // Load Community Models' Data
        const { docs } = await firestore.doc(`${COMM_COLL}/${communityId}`).collection("modelData").get();
        const modelData = docs.map((doc) => [doc.id, doc.data()]);

        const community = {
            communityId,
            communityDetails: mergeRunCapacityIntoCommunityDetails(
                communitySnapshot.data() || {},
                capacityResult
            ),
            modelDir: modelDirSnapshot.data() || {},
            modelData: { models: Object.fromEntries(modelData) } || { models: {} },
            ecmInputMap: ecmInputMapSnapshot.exists ? ecmInputMapSnapshot.data() : { ...DEFAULT_ECM_INPUT_MAP },
            parametricResults: parametricResults || {},
            // upgrades: upgradesSnapshot.data(),
            configuration: configurationSnapshot.data() || {},
        };

        dispatch(fetchCommunitySuccess(community));
    } catch (error) {
        console.log("Community does not exist:", error.message);
        dispatch(fetchCommunityError(error.message));
    }
};

// GET COMMUNITY MODEL (Details, Upgrades, and Results) used for Results tab
const getCommModel = (communityId, modelId) => async (dispatch) => {
    if (!communityId || !modelId) return null;

    const modelDetails = await firestore.doc(`${COMM_COLL}/${communityId}/modelDetails/${modelId}`).get();
    const modelUpgrades = await firestore
        .doc(`${COMM_COLL}/${communityId}/modelDetails/${modelId}/upgrades/data`)
        .get();
    const resultsSummary = await firestore.doc(`${COMM_COLL}/${communityId}/resultsSummary/${modelId}/`).get();

    return {
        details: modelDetails.data() || {},
        upgrades: modelUpgrades.data() || {},
        results: resultsSummary.data() || {},
    };
};

// GET AND RETURN COMMUNITY MODEL DATA
export const fetchCommModelDetails = (communityId, modelId) => (dispatch) => {
    return dispatch(getCommModel(communityId, modelId))
        .then((data) => data)
        .catch(() => dispatch(setCommunityError("Community does not exist")));
}; // returns data in a promise

// GET AND RETURN CUSTOM COMMUNITY RUN HISTORY AND RESULTS
export const fetchCommunityRunHistory = (communityId) => async (dispatch) => {
    const { docs: historyDocs } = await firestore.collection(`${COMM_COLL}/${communityId}/runHistory`).get();
    const { docs: resultsDocs } = await firestore.collection(`${COMM_COLL}/${communityId}/runResults`).get();

    const runHistory = historyDocs.map((doc) => [doc.id, doc.data()]);
    const runResults = resultsDocs.map((doc) => [doc.id, doc.data()]);

    return {
        batchIds: runHistory.map(([batchId, batchDetails]) => batchId),
        runHistory: Object.fromEntries(runHistory),
        runResults: Object.fromEntries(runResults),
    };
};

export const fetchCommunityRunResultsByMeasure = (communityId) => async () => {
    try {
        const runResultsByMeasure = await firestore.collection(`${COMM_COLL}/${communityId}/runResultsByMeasure`).get();

        return runResultsByMeasure.docs.map((doc) => doc.data());
    } catch (error) {
        console.log("Error fetching community run results by measure", error.message);
    }
};

export const deleteCommunity = (communityId, userUid) => async (dispatch) => {
    try {
        dispatch(deleteCommunityStart());

        // Remove from communities in backend
        await firestore.doc(`${COMM_COLL}/${communityId}`).delete();

        // Remove from user in backend
        await firestore
            .doc(`${USERS_COLL}/${userUid}`)
            .collection("commDir")
            .doc("directory")
            .update({
                [`communities.${communityId}`]: fieldValue.delete(),
            });

        // await dispatch(fetchUserCommDir(userUid, true)); // deleted one elsewhere, find it

        dispatch(deleteCommunitySuccess());
    } catch (error) {
        console.log("Error deleting community", error.message);
        dispatch(deleteCommunityError(error));
    }
};

export const batchDeleteCommunities = (communitiesToDelete, userUid) => async (dispatch) => {
    try {
        dispatch(batchDeleteCommunityStart());

        for await (const id of communitiesToDelete) {
            await dispatch(deleteCommunity(id, userUid));
        }

        dispatch(batchDeleteCommunitySuccess());
    } catch (error) {
        console.log("Error deleting multiple communities:", error.message);
        dispatch(batchDeleteCommunityError(error));
    }
};

// const batchDeleteComms = (communitiesToDeleteIds, uid) => async (dispatch) => {
//     try {
//         dispatch(startCommunityLoading());

//         const chunks = [];
//         for (let i = 0; i < communitiesToDeleteIds.length; i += 10) {
//             chunks.push(communitiesToDeleteIds.slice(i, i + 10));
//         }

//         // Iterate over each chunk and perform batch delete operations
//         for (const chunk of chunks) {
//             // Create a batch
//             const batch = firestore.batch();

//             // Delete each community and remove it from the user's community directory
//             chunk.forEach((communityId) => {
//                 // Delete community document
//                 const commRef = firestore.doc(`${COMM_COLL}/${communityId}`);
//                 batch.delete(commRef);

//                 // Remove community from user's community directory
//                 const userDirRef = firestore.doc(`${USERS_COLL}/${uid}/commDir/directory`);
//                 batch.update(userDirRef, {
//                     [`communities.${communityId}`]: fieldValue.delete(),
//                 });
//             });

//             // Commit the batch
//             await batch.commit();
//         }

//         dispatch(endCommunityLoading());
//     } catch (error) {
//         console.error("Error deleting multiple communities:", error);
//         dispatch(setCommunityError(error.message));
//     }
// };

export const moveCommunity =
    (communityId, userUid, moveToId, skipFetch = false) =>
    async (dispatch) => {
        try {
            dispatch(moveCommunityStart());

            await firestore
                .doc(`${USERS_COLL}/${userUid}`)
                .collection("commDir")
                .doc("directory")
                .update({
                    [`communities.${communityId}.parentFolderId`]: moveToId,
                });

            await dispatch(fetchUserCommDir(userUid, skipFetch));

            dispatch(moveCommunitySuccess());
        } catch (error) {
            console.log("error", error.message);
            dispatch(moveCommunityError(error));
        }
    };

// DUPLICATE COMMUNITY
export const duplicateCommunity = (communityId, uid, folderId, duplicateName) => (dispatch) => {
    dispatch(startCommunityLoading());

    // return duplicateCommData({ communityId, uid, folderId, duplicateName })
    // .then((data) => dispatch(fetchUserCommDir(uid)))
    // .then(() => dispatch({ type: types.DUPLICATE_COMM_SUCCESS }))
    // .catch((err) => dispatch({ type: types.DUPLICATE_COMM_ERROR, payload: err }));
};

// export const duplicateCommData = async ({ communityId, uid, folderId, duplicateName }) => {
//     return DashboardAPI({
//         method: "POST",
//         url: "/duplicateCommData",
//         data: {
//             communityId,
//             uid,
//             folderId,
//             duplicateName,
//         },
//     });
// };

/* COMMUNITY MODEL */
// Get model details for modelDir and screenshot of data to for modelData
export const fetchModel = (modelId) => async (dispatch) => {
    if (!modelId) return null;

    const metaData = (await firestore.doc(`${MODEL_COLL}/${modelId}`).get()).data();
    const data = (await firestore.doc(`${MODEL_COLL}/${modelId}/modelData/data`).get()).data();

    const {
        modelDetails: { name, lastEdited, createdAt, lastCliRun: { cliId } = {} },
    } = metaData;
    const {
        location: {
            weather: { location, region },
        },
        specifications: { facingDirection = {} },
    } = data;

    var status = "unknown";
    const runId = getBaseRunId(cliId);

    if (runId) {
        await getCliResults({ cliId: runId, modelId }).then((data) => {
            // status = "ready";
            if (data instanceof Error) {
                status = "error";
            } else {
                status = "ready";
            }
        });
        // .catch((err) => {
        //     status = "error";
        // });
    }

    const directions = ["S", "SE", "E", "NE", "N", "NW", "W", "SW"];

    var initDirections = {
        S: 0,
        SE: 0,
        E: 0,
        NE: 0,
        N: 0,
        NW: 0,
        W: 0,
        SW: 0,
    };
    initDirections[directions[facingDirection?.id]] = 1;

    return {
        modelDetails: {
            name: name,
            location: {
                locationId: location.id,
                regionId: region.id,
                regionCode: getProvCode(region.id),
            },
            countDirection: {
                baseDirection: facingDirection?.id,
                copyDirections: initDirections,
                duplicates: 1,
            },
            status: status,
            lastEdited: lastEdited || createdAt,
        },
        modelData: data,
    };
};

// Add community model to community
export const addCommunityModel = (modelId, endLoading) => async (dispatch) => {
    try {
        dispatch(startCommunityLoading());
        return await dispatch(fetchModel(modelId)).then(async ({ modelDetails, modelData }) => {
            // Update state: regions in communityDetails, and model in modelDir
            await dispatch(addCommModel({ modelId, modelDetails, modelData }));
            if (endLoading) {
                dispatch(endCommunityLoading());
            }
            // console.log(modelDetails, modelData)
            return { modelDetails, modelData };
        });
    } catch (error) {
        dispatch(setCommunityError(error));
    }
};

// Remove one community model
export const removeCommunityModel = (modelId, regionCode) => async (dispatch) => {
    try {
        dispatch(startCommunityLoading());
        dispatch(removeCommModel({ modelId, regionCode }));
        dispatch(endCommunityLoading());
    } catch (error) {
        console.error("Error deleting community:", error);
        dispatch(setCommunityError(error.message));
    }
};
// Remove multiple community models
export const batchRemoveCommModels = (modelsToRemoveIds) => async (dispatch) => {
    try {
        dispatch(startCommunityLoading());

        const chunks = [];

        for (let i = 0; i < modelsToRemoveIds.length; i += 10) {
            chunks.push(modelsToRemoveIds.slice(i, i + 10));
        }

        // Iterate over each chunk and perform batch delete operations
        for (const chunk of chunks) {
            // Create a batch
            const batch = firestore.batch();

            chunk.forEach(({ modelId, regionCode }) => {
                dispatch(removeCommModel({ modelId, regionCode }));
            });

            // Commit the batch
            await batch.commit();
        }

        dispatch(endCommunityLoading());
    } catch (error) {
        console.error("Error deleting multiple community models:", error);
        dispatch(setCommunityError(error.message));
    }
};

export const renameCommunity = (newName) => (dispatch) => {
    dispatch(startCommunityLoading());
    dispatch(setCommunityName(newName));
    dispatch(endCommunityLoading());
};

/* Community's user defined weather and location information (object) */

// export const updateCommunityLocation = (weatherLocation) => async (dispatch) => {
//     dispatch(setLocation(weatherLocation));
// };

/* Count directions for community model and its duplicates */

export const updateModelDuplicates = (modelId, copyDirections) => (dispatch) => {
    dispatch(startCommunityLoading());
    dispatch(setModelDuplicates(modelId, copyDirections));
    return dispatch(endCommunityLoading());
};

/* Time last edited at */
export const updateCommunityLastEdited = (uid, communityId, lastEdited) => (dispatch) => {
    dispatch(setLastEdited(lastEdited));

    // Update user's commDir in database
    firestore
        .doc(`${USERS_COLL}/${uid}`)
        .collection("commDir")
        .doc("directory")
        .update({
            [`communities.${communityId}.lastEdited`]: lastEdited,
        });

    // Update communities in database
    return firestore.doc(`${COMM_COLL}/${communityId}`).update({
        [`lastEdited`]: lastEdited,
    });
};

export const saveCommunity = (uid) => async (dispatch, getState) => {
    try {
        dispatch(saveCommunityStart());

        const {
            community: {
                communityId,
                community: {
                    communityDetails = {},
                    modelDir = { models: {} },
                    modelData = { models: {} },
                    configuration = {},
                },
                communityToCompare: {
                    communityDetails: initCommunityDetails = {},
                    modelDir: initModelDir = { models: {} },
                    modelData: { models: initModels = {} },
                    configuration: initConfiguration = {},
                },
            } = {},
        } = getState();

        const { name, regions, totalModels, weatherLocation, uiSettings } = communityDetails;
        const { models = {} } = modelData;

        const modelDataToAdd = Object.entries(models).filter(([key, value]) => !Object.keys(initModels).includes(key));

        const modelDataToRemove = Object.entries(initModels).filter(
            ([key, value]) => !Object.keys(models).includes(key)
        );

        // Update Community Data
        await firestore.doc(`${COMM_COLL}/${communityId}`).set(
            {
                [`name`]: name,
                [`regions`]: regions,
                [`totalModels`]: totalModels,
                [`weatherLocation`]: weatherLocation,
                [`uiSettings`]: uiSettings || {},
            },
            { merge: true }
        );

        // Update User
        await firestore
            .doc(`${USERS_COLL}/${uid}`)
            .collection("commDir")
            .doc("directory")
            .update({
                [`communities.${communityId}.name`]: name,
                [`communities.${communityId}.totalModels`]: totalModels,
                /* Community's region for displaying on directory table, e.g. "CAN", "ON" */
                [`communities.${communityId}.region`]: isEmpty(weatherLocation)
                    ? getRegion(regions)
                    : getProvCode(weatherLocation.location.weather.region.id),
            });

        // Update Model Dir (includes model duplicates)
        await firestore.doc(`${COMM_COLL}/${communityId}`).collection("modelDir").doc("directory").set(modelDir);

        // Update Model Data
        await Promise.all(
            modelDataToAdd.map(([modelId, data]) =>
                firestore.doc(`${COMM_COLL}/${communityId}/modelData/${modelId}`).set(data, { merge: true })
            )
        );

        await Promise.all(
            modelDataToRemove.map(([modelId]) =>
                firestore.doc(`${COMM_COLL}/${communityId}/modelData/${modelId}`).delete()
            )
        );

        // Update configuration with fuel costs
        await firestore
            .doc(`${COMM_COLL}/${communityId}`)
            .collection("config")
            .doc("configuration")
            .set(configuration, { merge: true });

        // Update initial snapshot and last edited time
        const updatedLastEdited = moment().format();
        dispatch(
            setInitData({
                communityDetails: { ...communityDetails, lastEdited: updatedLastEdited },
                modelDir: modelDir,
                modelData: modelData,
                configuration: configuration,
            })
        );
        dispatch(updateCommunityLastEdited(uid, communityId, updatedLastEdited));
        dispatch(saveCommunitySuccess());
    } catch (error) {
        console.error("Error saving community:", error);
        dispatch(setCommunityError(error.message));
    }
};

export const saveEcmInputMap = (communityId) => async (dispatch, getState) => {
    const LOG_PREFIX = "[saveEcmInputMap]";
    try {
        dispatch(saveEcmInputMapStart());

        const { community: { community: { ecmInputMap } = {} } = {}, commUpgrades: { packages = {} } = {} } =
            getState();

        const packageIdsInRedux = Object.keys(packages || {});
        let nonNoneCountBefore = 0;
        Object.keys(ecmInputMap || {}).forEach((catId) => {
            Object.keys(ecmInputMap[catId] || {}).forEach((upgradeId) => {
                if (upgradeId !== "None") nonNoneCountBefore += 1;
            });
        });

        // console.log(`${LOG_PREFIX} start`, {
        //     communityId,
        //     packageIdsInRedux,
        //     packageCount: packageIdsInRedux.length,
        //     nonNoneEcmRowsBeforePrune: nonNoneCountBefore,
        // });

        // Create a deep copy to avoid mutating Redux state (which is frozen/immutable)
        const ecmInputMapCopy = cloneDeep(ecmInputMap);

        /** @type {{ reason: string, catId: string, upgradeId: string, targetPackageId: string, detail?: unknown }[]} */
        const pruneEvents = [];

        //Need to iterate over ecmInputMap options and remove packages that are not present in the packages object
        Object.keys(ecmInputMapCopy).forEach((catId) => {
            Object.keys(ecmInputMapCopy[catId]).forEach((upgradeId) => {
                if (upgradeId !== "None") {
                    const row = ecmInputMapCopy?.[catId]?.[upgradeId];
                    const targetPackageId = row?.packageValues?.upgradePackageId || "";
                    const packageValuesKeys = row?.packageValues ? Object.keys(row.packageValues) : [];

                    const { [targetPackageId]: targetPackage = {} } = packages || {};

                    if (!targetPackageId || isEmpty(targetPackage)) {
                        const reason = !targetPackageId
                            ? "missing_packageValues.upgradePackageId"
                            : "package_not_in_redux_packages";
                        pruneEvents.push({
                            reason,
                            catId,
                            upgradeId,
                            targetPackageId: targetPackageId || "(empty)",
                            detail: {
                                packageValuesKeys,
                                hasPackageValues: !!row?.packageValues,
                                upgradeKeysUnderPackage: targetPackageId
                                    ? Object.keys(packages[targetPackageId]?.upgrades || {})
                                    : [],
                            },
                        });
                        delete ecmInputMapCopy[catId][upgradeId];
                    } else if (!isEmpty(targetPackage)) {
                        const targetUpgrade = targetPackage?.upgrades?.[upgradeId] || {};
                        const upgradeKeysInPackage = Object.keys(targetPackage?.upgrades || {});
                        if (isEmpty(targetUpgrade)) {
                            pruneEvents.push({
                                reason: "ecm_upgradeId_not_found_under_package.upgrades",
                                catId,
                                upgradeId,
                                targetPackageId,
                                detail: {
                                    upgradeKeysInPackageSample: upgradeKeysInPackage.slice(0, 15),
                                    upgradeKeysInPackageCount: upgradeKeysInPackage.length,
                                },
                            });
                            delete ecmInputMapCopy[catId][upgradeId];
                        }
                    }
                }
            });
        });

        let nonNoneCountAfter = 0;
        Object.keys(ecmInputMapCopy || {}).forEach((catId) => {
            Object.keys(ecmInputMapCopy[catId] || {}).forEach((upgradeId) => {
                if (upgradeId !== "None") nonNoneCountAfter += 1;
            });
        });

        // if (pruneEvents.length > 0) {
        //     console.warn(`${LOG_PREFIX} pruned ${pruneEvents.length} ecm row(s) — inspect reasons`, pruneEvents);
        // } else {
        //     console.log(`${LOG_PREFIX} prune: no rows removed`);
        // }

        // console.log(`${LOG_PREFIX} summary`, {
        //     nonNoneEcmRowsAfterPrune: nonNoneCountAfter,
        //     removedCount: nonNoneCountBefore - nonNoneCountAfter,
        // });

        const ecmInputMapNormalized = setAllCommunityEcmMeasuresActive(ecmInputMapCopy);

        await firestore
            .doc(`${COMM_COLL}/${communityId}`)
            .collection("config")
            .doc("ecmInputMap")
            .set(ecmInputMapNormalized); //no merge because we need to delete parts

        dispatch(saveEcmInputMapSuccess(ecmInputMapNormalized));
        return ecmInputMapNormalized;
    } catch (error) {
        console.log("error", error.message);
        dispatch(saveEcmInputMapError(error));
        return null;
    }
};

export const watchCommunityUpdates = (communityId) => (dispatch) => {
    const unsubCommunityDoc = firestore.doc(`${COMM_COLL}/${communityId}`).onSnapshot((doc) => {
        dispatch(updateCommunityDetails(doc.data()));
    });

    const unsubEcmInputMap = firestore
        .doc(`${COMM_COLL}/${communityId}`)
        .collection("config")
        .doc("ecmInputMap")
        .onSnapshot((doc) => {
            if (doc.exists && doc.data()) {
                dispatch(mergeCommunityEcmValidationResults(doc.data()));
            }
        });

    return () => {
        unsubCommunityDoc();
        unsubEcmInputMap();
    };
};

export const watchRunResultsUpdates = (communityId) => (dispatch) => {
    const unsubResults = firestore.collection(`${COMM_COLL}/${communityId}/runResults`).onSnapshot((snapshot) => {
        dispatch(updateRunResults(snapshot.docs.map((doc) => doc.data())));
    });

    return () => {
        unsubResults();
    };
};
